/**
 * Guides Page - Dashboard Guide with sections matching tabs
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OwnerDashboardGuide from '../../components/owner/OwnerDashboardGuide.jsx';
import TouristTaxesGuide from '../../components/owner/TouristTaxesGuide.jsx';
import { useSession } from '../../contexts/SessionContext.jsx';
import { SkeletonCard, SkeletonText } from '../../components/shared/SkeletonLoader.jsx';
import ContextBar from '../../components/shared/ContextBar.jsx';
import OwnerModuleCard from '../../components/shared/OwnerModuleCard.jsx';

export default function GuidesPage({ onLogout }) {
  const { effectiveMode, selectedProperty } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Scroll to anchor if present in hash
  const scrollToAnchor = (anchorId) => {
    const element = document.getElementById(anchorId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (location.hash) {
      const anchorId = location.hash.substring(1);
      setTimeout(() => scrollToAnchor(anchorId), 100);
    }
  }, [location.hash]);

  const guideSections = [
    { id: 'calendar', title: 'Calendar', description: 'Understanding your booking calendar and upcoming events' },
    { id: 'reservations', title: 'Reservations', description: 'Managing and viewing your reservations' },
    { id: 'analytics', title: 'Analytics', description: 'Revenue and reservation analytics' },
    { id: 'financials', title: 'Financials', description: 'Financial breakdown and tourist tax information' },
    { id: 'property-activity', title: 'Property Activity', description: 'Non-booking operations and maintenance' },
    { id: 'links-reports', title: 'Links & Reports', description: 'External links and downloadable reports' },
  ];

  return (
    <div className="owner-dashboard-container">
      <ContextBar title={selectedProperty?.name || 'Property'} subtitle="Guides and explanations for metrics, fees, and taxes" />

      {/* Topics Navigation */}
      <OwnerModuleCard title="Topics" subtitle="Quick navigation to help sections">
        <div style={styles.topicsGrid}>
          {guideSections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/guides#${section.id}`);
                scrollToAnchor(section.id);
              }}
              style={styles.topicLink}
            >
              <div style={styles.topicTitle}>{section.title}</div>
              <div style={styles.topicDescription}>{section.description}</div>
            </a>
          ))}
        </div>
      </OwnerModuleCard>

      {/* Guide Content */}
      <div>
        {guideSections.map((section) => (
          <OwnerModuleCard 
            key={section.id} 
            id={section.id}
            title={section.title}
            subtitle={section.description}
          >
            {section.id === 'financials' && (
              <>
                <div id="tourist-tax-ledger" style={{ marginTop: 'var(--spacing-4)' }}>
                  <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-2)' }}>Financial Breakdown</h3>
                  <p style={{ marginBottom: 'var(--spacing-3)' }}>
                    Your financial breakdown shows both a consolidated summary and a per-booking breakdown. Here's what each component means:
                  </p>
                  <ul style={{ marginLeft: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
                    <li><strong>Base Rent:</strong> The agreed-upon rental amount for the property.</li>
                    <li><strong>Cleaning Fees:</strong> Fees charged to guests for cleaning services.</li>
                    <li><strong>Guest Service Fee:</strong> Platform fees charged by booking channels (Airbnb, VRBO, Hostaway). This offsets channel fees; you are not penalized.</li>
                    <li><strong>Tourist Tax:</strong> State and county taxes collected from guests.</li>
                    <li><strong>Markup:</strong> Additional amount that offsets channel fees and ensures you receive the agreed base rent.</li>
                    <li><strong>Gross Revenue:</strong> Total revenue from all bookings before any deductions.</li>
                    <li><strong>Net Revenue:</strong> Revenue after platform fees and cleaning fees are deducted.</li>
                    <li><strong>Amount Deposited:</strong> Total amount deposited to your account after all deductions.</li>
                  </ul>
                  <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}>Tourist Taxes</h3>
                  <p style={{ marginBottom: 'var(--spacing-3)' }}>
                    Tourist taxes are withdrawn on the 10th of each month. The timing of withdrawal varies by booking channel:
                  </p>
                  <ul style={{ marginLeft: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
                    <li><strong>Airbnb:</strong> Taxes are withdrawn before your deposit is processed.</li>
                    <li><strong>VRBO / Hostaway:</strong> Taxes are withdrawn after your deposit is processed.</li>
                  </ul>
                  <p style={{ marginBottom: 'var(--spacing-3)' }}>
                    You can view all tourist tax transactions in the Tourist Tax Ledger section below.
                  </p>
                </div>
                <TouristTaxesGuide taxConfig={{}} />
              </>
            )}
            {section.id === 'calendar' && (
              <>
                <OwnerDashboardGuide />
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-2)' }}>Using the Calendar</h3>
                  <p style={{ marginBottom: 'var(--spacing-3)' }}>
                    The Calendar tab is your primary dashboard view. It shows:
                  </p>
                  <ul style={{ marginLeft: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
                    <li><strong>Monthly Calendar:</strong> View all bookings for the selected month. Click on any booking to see details.</li>
                    <li><strong>Upcoming Events:</strong> Filter by check-ins and check-outs in the next 7 days. Click an event to open booking details.</li>
                    <li><strong>Tourist Tax Notices:</strong> If you have upcoming reservations with tourist taxes due, you'll see a notice that links to the Tourist Tax Ledger.</li>
                  </ul>
                  <p style={{ marginBottom: 'var(--spacing-3)' }}>
                    Use the period selector in the header to view different months. The calendar updates automatically to show bookings for the selected period.
                  </p>
                </div>
              </>
            )}
            {/* Additional guide content can be added here */}
          </OwnerModuleCard>
        ))}
      </div>
    </div>
  );
}

// Styles removed - now using CSS classes from owner-ui.css

